import { Link, useParams } from 'react-router-dom'

import styles from '../app.module.css'
import { useAsync } from '../lib/useAsync.js'
import { invoicesApi, INVOICE_STATUS_LABEL, LINE_TYPE_LABEL } from '../lib/invoices.js'

/**
 * 기업 청구 상세 (BIZ-16). 조회 + 거래명세서/청구서 인쇄·PDF 저장(BIZ-61).
 * PDF는 별도 라이브러리 없이 브라우저 인쇄(→ 'PDF로 저장')로 낸다 — 한글 폰트를 브라우저가
 * 처리하고 산출물이 화면과 일치한다. 인쇄 시 @media print가 앱 크롬을 걷어내고 문서만 남긴다.
 * 발행처(공급자)는 '포비즈'로 표기하고, 공급받는 자(기업)의 식별정보를 서버에서 받아 채운다.
 */

function won(n: number): string {
  return `${n.toLocaleString('ko-KR')}원`
}
function fmtDate(s: string | null): string {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('ko-KR')
}
function todayKst(): string {
  return new Intl.DateTimeFormat('ko-KR').format(new Date())
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useAsync(() => invoicesApi.detail(Number(id)), [id])

  if (loading) return <main><p className={styles.muted}>불러오는 중…</p></main>
  if (error || !data) {
    return (
      <main>
        <div className={styles.error} role="alert">{error ?? '청구서를 불러오지 못했습니다.'}</div>
        <Link to="/invoices" className={styles.link}>← 청구 내역으로</Link>
      </main>
    )
  }

  const inv = data.invoice
  const issued = fmtDate(inv.issuedAt) || todayKst()

  return (
    <main>
      {/* 화면 전용 툴바 — 인쇄물에선 숨긴다(@media print). */}
      <div className={styles.docToolbar}>
        <Link to="/invoices" className={styles.link}>← 청구 내역으로</Link>
        <button data-variant="primary" onClick={() => window.print()}>
          인쇄 / PDF 저장
        </button>
      </div>

      <article className={styles.doc}>
        <header className={styles.docHead}>
          <div>
            <div className={styles.docBrand}>포비즈</div>
            <h1 className={styles.docTitle}>거래명세서 · 청구서</h1>
          </div>
          <dl className={styles.docMeta}>
            <dt>청구 월</dt><dd>{inv.billingMonth}</dd>
            <dt>상태</dt><dd>{INVOICE_STATUS_LABEL[inv.status]}</dd>
            <dt>발행일</dt><dd>{issued}</dd>
            {inv.dueAt ? (<><dt>지급 기한</dt><dd>{fmtDate(inv.dueAt)}</dd></>) : null}
          </dl>
        </header>

        <section className={styles.party}>
          <div className={styles.partyLabel}>공급받는 자</div>
          <dl className={styles.dl}>
            <dt className={styles.dt}>상호</dt>
            <dd className={styles.dd}>{inv.companyName ?? '-'}</dd>
            <dt className={styles.dt}>사업자등록번호</dt>
            <dd className={styles.dd}>{inv.bizRegNo ?? '-'}</dd>
            <dt className={styles.dt}>대표자</dt>
            <dd className={styles.dd}>{inv.ceoName ?? '-'}</dd>
            <dt className={styles.dt}>주소</dt>
            <dd className={styles.dd}>{inv.companyAddress ?? '-'}</dd>
          </dl>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>청구 금액</h2>
          <div className={styles.infoCard}>
            <dl className={styles.dl}>
              <dt className={styles.dt}>소계</dt>
              <dd className={styles.dd}>{won(inv.subtotal)}</dd>
              {inv.adjustmentTotal !== 0 ? (
                <>
                  <dt className={styles.dt}>조정</dt>
                  <dd className={styles.dd}>{won(inv.adjustmentTotal)}</dd>
                </>
              ) : null}
              <dt className={styles.dt}>청구 합계</dt>
              <dd className={styles.dd}><strong>{won(inv.totalAmount)}</strong></dd>
              {inv.dueAt ? (
                <>
                  <dt className={styles.dt}>지급 기한</dt>
                  <dd className={styles.dd}>{fmtDate(inv.dueAt)}</dd>
                </>
              ) : null}
            </dl>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>청구 항목</h2>
          <div className={styles.tableScroll}>
            <table className={styles.docTable}>
              <thead>
                <tr>
                  <th scope="col" style={{ width: '14%' }}>구분</th>
                  <th scope="col">내용</th>
                  <th scope="col" style={{ width: '24%' }}>받는 곳</th>
                  <th scope="col" className={styles.num} style={{ width: '20%' }}>금액</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{LINE_TYPE_LABEL[l.lineType]}</td>
                    <td>{l.description}</td>
                    <td>{l.partner ?? '-'}</td>
                    <td className={styles.num}>{won(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>합계</td>
                  <td className={styles.num}>{won(inv.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <footer className={styles.docFoot}>본 명세서는 포비즈에서 발행되었습니다.</footer>
      </article>
    </main>
  )
}
